"use client";

import React, { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Popover, Transition } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/24/solid";
import Link from "next/link";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/app/lib/hooks";
import { getContinents } from "@/app/lib/reducers/continentReducer";
import { getCountries } from "@/app/lib/reducers/countriesReducer";
import useClickOutside from "@/hooks/useClickOutside";

interface Continent {
  id?: string | number;
  code: string;
  name: string;
  slug?: string;
  nameTranslated?: string;
}

interface Country {
  code: string;
  name: string;
  slug: string;
  continentId: string;
  numCities?: number;
  numTours?: number;
  nameTranslated?: string;
}

/**
 * Destinations megamenu — left rail of continents, right grid of countries
 * for the hovered/selected one. Both lists are dynamic:
 *   - Continents: `state.continent.continents` (dispatched via getContinents)
 *   - Countries:  `state.countries.countries` (dispatched via getCountries,
 *     all activated rows in one shot then grouped client-side by
 *     `continentId` matching continent `code`).
 *
 * Names use the localized `nameTranslated` field with `name` as fallback.
 */
const DestinationsMegaMenu: React.FC = () => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const { t } = useTranslation("guydeez");
  const selectedLanguage = useAppSelector(
    (s) => s.language.selectedLanguage || "fr_FR"
  );
  const continents: Continent[] = useAppSelector(
    (s: any) => s.continent?.continents ?? []
  );
  const countries: Country[] = useAppSelector(
    (s: any) => s.countries?.countries ?? []
  );
  const [activeCode, setActiveCode] = useState<string | null>(null);

  useEffect(() => {
    if (!continents.length) dispatch(getContinents(true, selectedLanguage));
    if (!countries.length) dispatch(getCountries(null, true, selectedLanguage));
  }, [dispatch, selectedLanguage, continents.length, countries.length]);

  // continentId (on country) matches continent.code
  const countriesByContinent = useMemo(() => {
    const map = new Map<string, Country[]>();
    countries.forEach((c) => {
      if (!c.continentId) return;
      const list = map.get(c.continentId) || [];
      list.push(c);
      map.set(c.continentId, list);
    });
    // Sort each list alphabetically by display name
    map.forEach((list) => {
      list.sort((a, b) =>
        (a.nameTranslated || a.name).localeCompare(b.nameTranslated || b.name)
      );
    });
    return map;
  }, [countries]);

  // Only show continents that actually have at least one country.
  const visibleContinents = useMemo(
    () =>
      continents.filter((c) => (countriesByContinent.get(c.code) || []).length > 0),
    [continents, countriesByContinent]
  );

  // Auto-select the first continent that has countries once data lands.
  useEffect(() => {
    if (!activeCode && visibleContinents.length) {
      setActiveCode(visibleContinents[0].code);
    }
  }, [activeCode, visibleContinents]);

  const handleClose = () => {
    const button = dropdownRef.current?.querySelector(
      'button[data-headlessui-state="open"]'
    ) as HTMLElement | null;
    button?.click();
  };
  useClickOutside(dropdownRef, handleClose);

  const activeContinent =
    visibleContinents.find((c) => c.code === activeCode) ||
    visibleContinents[0];
  const activeCountries = activeContinent
    ? countriesByContinent.get(activeContinent.code) || []
    : [];

  const displayContinent = (c: Continent) =>
    capitalize(c.nameTranslated) || c.name;
  const displayCountry = (c: Country) =>
    capitalize(c.nameTranslated) || c.name;

  return (
    <div ref={dropdownRef} className="relative flex">
      <Popover className="self-center" style={{ display: "contents" }}>
        {({ open, close }) => (
          <>
            <Popover.Button
              className={`group inline-flex items-center text-sm font-medium text-white/90 hover:text-white focus:outline-none px-2 py-2 ${
                open ? "text-white" : ""
              }`}
            >
              <span>{t("nav_destinations", { defaultValue: "Destinations" })}</span>
              <ChevronDownIcon
                className={`ml-1 h-4 w-4 transition-transform ${open ? "-rotate-180" : ""}`}
                aria-hidden="true"
              />
            </Popover.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="opacity-0 translate-y-1"
              enterTo="opacity-100 translate-y-0"
              leave="transition ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0"
              leaveTo="opacity-0 translate-y-1"
            >
              <Popover.Panel className="absolute left-0 top-full z-40 mt-3 w-[760px]">
                <div className="rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 overflow-hidden">
                  {visibleContinents.length === 0 ? (
                    <div className="p-8 text-sm text-neutral-500">
                      {t("nav_loading", { defaultValue: "Chargement..." })}
                    </div>
                  ) : (
                    <div className="grid grid-cols-[260px_1fr]">
                      {/* Continents column */}
                      <div className="bg-neutral-50 p-4">
                        <Link
                          href={"/continents" as any}
                          onClick={() => close()}
                          className="block text-[10px] font-bold uppercase tracking-widest text-neutral-500 hover:text-[#C13B5C] px-3 mb-2 transition-colors"
                        >
                          {t("nav_continents", { defaultValue: "Continents" })}{" "}
                          →
                        </Link>
                        <ul className="flex flex-col gap-1">
                          {visibleContinents.map((c) => {
                            const list = countriesByContinent.get(c.code) || [];
                            const countryCount = list.length;
                            const tourCount = list.reduce(
                              (acc, co) => acc + (co.numTours || 0),
                              0
                            );
                            const active = c.code === activeContinent?.code;
                            return (
                              <li key={c.code}>
                                <button
                                  type="button"
                                  onMouseEnter={() => setActiveCode(c.code)}
                                  onFocus={() => setActiveCode(c.code)}
                                  onClick={() => setActiveCode(c.code)}
                                  className={`w-full text-left px-3 py-2 rounded-lg flex items-center justify-between transition-colors ${
                                    active
                                      ? "bg-orange-50 text-[#5B1F3A]"
                                      : "hover:bg-white text-neutral-800"
                                  }`}
                                >
                                  <div>
                                    <div className="text-sm font-semibold">
                                      {displayContinent(c)}
                                    </div>
                                    <div className="text-[11px] text-neutral-500 mt-0.5">
                                      {countryCount} pays · {tourCount} tours
                                    </div>
                                  </div>
                                  <ChevronDownIcon className="w-4 h-4 -rotate-90 text-neutral-400" />
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>

                      {/* Countries column */}
                      <div className="p-5">
                        <div className="flex items-center justify-between mb-3 px-1">
                          <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">
                            {t("nav_countries", { defaultValue: "Pays" })}
                            {activeContinent
                              ? ` · ${displayContinent(activeContinent)}`
                              : ""}
                          </div>
                          {activeContinent?.slug && (
                            <Link
                              href={`/continent/${activeContinent.slug}` as any}
                              onClick={() => close()}
                              className="text-xs font-semibold text-[#C13B5C] hover:underline"
                            >
                              {t("nav_see_all", { defaultValue: "Tout voir →" })}
                            </Link>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-x-6 gap-y-3 max-h-[420px] overflow-y-auto pr-1">
                          {activeCountries.map((co) => (
                            <Link
                              key={co.code}
                              href={`/country/${co.slug}` as any}
                              onClick={() => close()}
                              className="flex items-center gap-3 px-1 py-1.5 hover:bg-neutral-50 rounded-md"
                            >
                              <Flag code={co.code} alt={displayCountry(co)} />
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-semibold text-neutral-900 truncate">
                                  {displayCountry(co)}
                                </div>
                                {co.numTours ? (
                                  <div className="text-[11px] text-neutral-500">
                                    {co.numTours}{" "}
                                    {co.numTours === 1 ? "tour" : "tours"}
                                  </div>
                                ) : null}
                              </div>
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>
    </div>
  );
};

// nameTranslated comes lowercased from the API ("afrique", "asie", ...).
// Force a single capital letter so the menu doesn't read like an email.
const capitalize = (s?: string) => {
  if (!s) return "";
  return s
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");
};

/**
 * Country flag SVG sourced from flagcdn.com, which serves square-cropped
 * SVGs keyed by lowercase ISO-2 country code (matches our `country.code`).
 * Free, no API key, CC0 — drops gracefully to nothing if the code is bad.
 */
const Flag: React.FC<{ code: string; alt: string }> = ({ code, alt }) => {
  const c = (code || "").toLowerCase();
  if (!c || c.length !== 2) return null;
  return (
    <img
      src={`https://flagcdn.com/${c}.svg`}
      alt={alt}
      width={20}
      height={15}
      loading="lazy"
      style={{
        width: 20,
        height: 15,
        objectFit: "cover",
        borderRadius: 2,
        flexShrink: 0,
        boxShadow: "0 0 0 1px rgba(0,0,0,0.08)",
      }}
    />
  );
};

export default DestinationsMegaMenu;
